import React, { useMemo, useState } from 'react';
import type { ColDef } from 'ag-grid-community';
import { CheckCircle2, RefreshCw } from 'lucide-react';
import { LopAgGrid } from '../../components/LopAgGrid';
import { useAlerts } from '../../hooks/useAlerts';
import { runAlerts } from '../../api/alerts';
import { useAuth } from '../../contexts/AuthContext';
import { canEdit } from '../../utils/permissions';
import type { Alert } from '../../types/Alert';
import { LopButton } from '../../components/LopButton';
import { t } from '../../i18n/t';
import './AlertsDashboardPage.css';

// "שים לב" dashboard — current snapshot grouped by severity (decision A,
// approved by Nitzan). Layout:
//
//   ┌──────────────────────────────────────────────────────────────┐
//   │ 🔴 קריטי (count)                                              │
//   │   ┌──────────┐  ┌──────────┐  ... one tile per alertTypeId   │
//   │   │ 12       │  │ 3        │                                  │
//   │   │ <name>   │  │ <name>   │                                  │
//   │   └──────────┘  └──────────┘                                  │
//   ├──────────────────────────────────────────────────────────────┤
//   │ 🟠 חמור (count)  →  tiles                                     │
//   ├──────────────────────────────────────────────────────────────┤
//   │ 🔵 קל   (count)  →  tiles                                     │
//   └──────────────────────────────────────────────────────────────┘
//   ↓ click a tile  ↓
//   <drill-down AgGrid for that alertTypeId>
//
// Tiles are buttons; clicking selects the type and renders the filtered grid
// below. Click again on the same tile to close. Empty severity groups render
// nothing (no banner clutter — a fixed issue simply disappears).
//
// Empty state (alerts table is 0 rows): green CheckCircle + "no active alerts"
// message + a "run check" button (gated on sysadminTools:Edit — same gate as
// the SysadminToolsPage button). The button POSTs /api/alerts/run for the
// active tenant and refreshes the cache; if the SP finds issues, the empty
// view replaces itself with the tiles. Temporary convenience until an
// automated archiver job runs the SP on a schedule.

const SEVERITY_ORDER: ReadonlyArray<1 | 2 | 3> = [1, 2, 3];
const SEVERITY_LABEL: Record<1 | 2 | 3, string> = {
  1: t('alertTypes.severity.critical'),
  2: t('alertTypes.severity.severe'),
  3: t('alertTypes.severity.light'),
};
const SEVERITY_CLASS: Record<1 | 2 | 3, string> = {
  1: 'sev-critical',
  2: 'sev-severe',
  3: 'sev-light',
};

interface TypeBucket {
  alertTypeId: number;
  alertTypeName: string;
  severity: 1 | 2 | 3;
  rows: Alert[];
}

function group(alerts: Alert[]): Map<1 | 2 | 3, TypeBucket[]> {
  // First bucket by (severity, alertTypeId) — keep insertion order from the
  // backend (already severity asc, then alertTypeId, then alertId).
  const bySev = new Map<1 | 2 | 3, Map<number, TypeBucket>>();
  for (const a of alerts) {
    let inSev = bySev.get(a.severity);
    if (!inSev) {
      inSev = new Map();
      bySev.set(a.severity, inSev);
    }
    let bucket = inSev.get(a.alertTypeId);
    if (!bucket) {
      bucket = {
        alertTypeId: a.alertTypeId,
        alertTypeName: a.alertTypeName,
        severity: a.severity,
        rows: [],
      };
      inSev.set(a.alertTypeId, bucket);
    }
    bucket.rows.push(a);
  }
  const out = new Map<1 | 2 | 3, TypeBucket[]>();
  for (const sev of SEVERITY_ORDER) {
    const inner = bySev.get(sev);
    if (inner) out.set(sev, Array.from(inner.values()));
  }
  return out;
}

export function AlertsDashboardPage() {
  const { user } = useAuth();
  const canRun = canEdit(user, 'sysadminTools');
  const { data, loading, error, refresh } = useAlerts();
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);

  const grouped = useMemo(() => group(data ?? []), [data]);

  const selectedBucket = useMemo<TypeBucket | null>(() => {
    if (selectedTypeId === null) return null;
    // Array.from over Map.values() — tsconfig target is below es2015 (TS2802).
    const allBuckets = Array.from(grouped.values());
    for (const list of allBuckets) {
      const found = list.find((b) => b.alertTypeId === selectedTypeId);
      if (found) return found;
    }
    return null;
  }, [grouped, selectedTypeId]);

  const drillDownColumns = useMemo<ColDef<Alert>[]>(
    () => [
      { field: 'alertId', headerName: t('alerts.columns.alertId'), width: 100 },
      { field: 'fundId', headerName: t('alerts.columns.fundId'), width: 100 },
      { field: 'fundName', headerName: t('alerts.columns.fundName'), width: 180 },
      { field: 'assetId', headerName: t('alerts.columns.assetId'), width: 130 },
      { field: 'description', headerName: t('alerts.columns.description'), flex: 1 },
    ],
    [],
  );

  async function handleRunAlerts() {
    setRunning(true);
    setRunError(null);
    try {
      await runAlerts();
      await refresh();
    } catch (err: unknown) {
      setRunError(err instanceof Error ? err.message : String(err));
    } finally {
      setRunning(false);
    }
  }

  if (loading) return <div className="alerts-dashboard__status">{t('common.loading')}</div>;
  if (error) {
    return (
      <div className="alerts-dashboard__status">
        {error}{' '}
        <button type="button" onClick={() => refresh()} className="alerts-dashboard__retry">
          {t('alerts.retry')}
        </button>
      </div>
    );
  }
  if (!data || data.length === 0) {
    return (
      <div className="alerts-dashboard">
        <h1 className="alerts-dashboard__title">{t('alerts.pageTitle')}</h1>
        <div className="alerts-dashboard__empty-state">
          <CheckCircle2
            size={56}
            strokeWidth={1.5}
            className="alerts-dashboard__empty-icon"
            aria-hidden="true"
          />
          <div className="alerts-dashboard__empty-text">{t('alerts.empty')}</div>
          {canRun && (
            <LopButton
              variant="secondary"
              icon={<RefreshCw size={16} aria-hidden="true" />}
              onClick={handleRunAlerts}
              loading={running}
            >
              {t('alerts.runRefresh')}
            </LopButton>
          )}
          {runError && (
            <div className="alerts-dashboard__empty-error">{runError}</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="alerts-dashboard">
      <h1 className="alerts-dashboard__title">
        {t('alerts.pageTitle')}{' '}
        <span className="alerts-dashboard__total">({data.length})</span>
      </h1>

      {SEVERITY_ORDER.map((sev) => {
        const buckets = grouped.get(sev);
        if (!buckets || buckets.length === 0) return null;
        const sevCount = buckets.reduce((sum, b) => sum + b.rows.length, 0);
        return (
          <section
            key={sev}
            className={`alerts-dashboard__section alerts-dashboard__section--${SEVERITY_CLASS[sev]}`}
          >
            <h2 className="alerts-dashboard__section-title">
              <span className={`alerts-dashboard__dot ${SEVERITY_CLASS[sev]}`} />
              {SEVERITY_LABEL[sev]}{' '}
              <span className="alerts-dashboard__section-count">({sevCount})</span>
            </h2>
            <div className="alerts-dashboard__tiles">
              {buckets.map((b) => {
                const active = selectedTypeId === b.alertTypeId;
                return (
                  <button
                    key={b.alertTypeId}
                    type="button"
                    className={`alerts-dashboard__tile ${SEVERITY_CLASS[sev]} ${
                      active ? 'is-active' : ''
                    }`}
                    onClick={() =>
                      setSelectedTypeId(active ? null : b.alertTypeId)
                    }
                    aria-pressed={active}
                  >
                    <div className="alerts-dashboard__tile-count">{b.rows.length}</div>
                    <div className="alerts-dashboard__tile-name">{b.alertTypeName}</div>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}

      {selectedBucket && (
        <section className="alerts-dashboard__drilldown">
          <h2 className="alerts-dashboard__drilldown-title">
            {selectedBucket.alertTypeName}{' '}
            <span className="alerts-dashboard__total">
              ({selectedBucket.rows.length})
            </span>
          </h2>
          <div className="alerts-dashboard__grid">
            <LopAgGrid<Alert>
              rowData={selectedBucket.rows}
              columnDefs={drillDownColumns}
              getRowId={(p: { data: Alert }) => String(p.data.alertId)}
            />
          </div>
        </section>
      )}
    </div>
  );
}
